import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import '../styles/AdminDashboard.css';

const AdminDashboard = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('courses');
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // 인증 및 권한 확인
  useEffect(() => {
    if (!loading && (!isAuthenticated || (user && user.role !== 'admin'))) {
      navigate('/login');
    }
  }, [isAuthenticated, user, loading, navigate]);

  // 강의 목록 가져오기
  const fetchCourses = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await api.get('/courses');
      setCourses(res.data);
    } catch (err) {
      setError('강의 목록을 불러오는데 실패했습니다.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // 사용자 목록 가져오기
  const fetchUsers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch (err) {
      setError('사용자 목록을 불러오는데 실패했습니다.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // 탭 변경 시 데이터 로드
  useEffect(() => {
    if (activeTab === 'courses') {
      fetchCourses();
    } else if (activeTab === 'users') {
      fetchUsers();
    }
  }, [activeTab]);

  // 강의 삭제
  const handleDeleteCourse = async (courseId) => {
    if (window.confirm('정말로 이 강의를 삭제하시겠습니까?')) {
      try {
        await api.delete(`/courses/${courseId}`);
        setCourses(courses.filter(course => course._id !== courseId));
      } catch (err) {
        setError('강의 삭제에 실패했습니다.');
        console.error(err);
      }
    }
  };

  // 강의 공개/비공개 전환
  const handleTogglePublish = async (courseId, isPublished) => {
    try {
      await api.put(`/courses/${courseId}`, { isPublished: !isPublished });
      setCourses(courses.map(course => 
        course._id === courseId ? { ...course, isPublished: !isPublished } : course
      ));
    } catch (err) {
      setError('강의 상태 변경에 실패했습니다.');
      console.error(err);
    }
  };

  if (loading || !user) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>로딩 중...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>관리자 대시보드</h1>
        <p>안녕하세요, {user.name}님! 관리자 페이지에 오신 것을 환영합니다.</p>
      </div>

      <div className="admin-tabs">
        <button 
          className={`tab-button ${activeTab === 'courses' ? 'active' : ''}`}
          onClick={() => setActiveTab('courses')}
        >
          강의 관리
        </button>
        <button 
          className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          사용자 관리
        </button>
        <button 
          className={`tab-button ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          통계
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {activeTab === 'courses' && (
        <div className="courses-management">
          <div className="management-header">
            <h2>강의 관리</h2>
            <Link to="/admin/courses/new" className="btn btn-primary">
              새 강의 추가
            </Link>
          </div>

          {isLoading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>강의 목록을 불러오는 중...</p>
            </div>
          ) : courses.length === 0 ? (
            <div className="empty-state">
              <p>등록된 강의가 없습니다.</p>
            </div>
          ) : (
            <div className="courses-table-container">
              <table className="courses-table">
                <thead>
                  <tr>
                    <th>제목</th>
                    <th>카테고리</th>
                    <th>가격</th>
                    <th>상태</th>
                    <th>생성일</th>
                    <th>액션</th>
                  </tr>
                </thead>
                <tbody>
                  {courses.map(course => (
                    <tr key={course._id}>
                      <td>{course.title}</td>
                      <td>{course.category}</td>
                      <td>{course.price.toLocaleString()}원</td>
                      <td>
                        <span className={`status ${course.isPublished ? 'published' : 'draft'}`}>
                          {course.isPublished ? '공개' : '비공개'}
                        </span>
                      </td>
                      <td>{new Date(course.createdAt).toLocaleDateString()}</td>
                      <td className="actions">
                        <Link to={`/admin/courses/edit/${course._id}`} className="btn btn-small btn-edit">
                          수정
                        </Link>
                        <button 
                          className={`btn btn-small ${course.isPublished ? 'btn-unpublish' : 'btn-publish'}`}
                          onClick={() => handleTogglePublish(course._id, course.isPublished)}
                        >
                          {course.isPublished ? '비공개로 전환' : '공개로 전환'}
                        </button>
                        <button 
                          className="btn btn-small btn-delete"
                          onClick={() => handleDeleteCourse(course._id)}
                        >
                          삭제
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'users' && (
        <div className="users-management">
          <h2>사용자 관리</h2>
          
          {isLoading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>사용자 목록을 불러오는 중...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="empty-state">
              <p>등록된 사용자가 없습니다.</p>
            </div>
          ) : (
            <div className="users-table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>이름</th>
                    <th>이메일</th>
                    <th>역할</th>
                    <th>가입일</th>
                    <th>수강 중인 강의</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user._id}>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>{user.role === 'admin' ? '관리자' : '학생'}</td>
                      <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                      <td>{user.enrolledCourses?.length || 0}개</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'stats' && (
        <div className="stats-management">
          <h2>통계</h2>
          <div className="stats-cards">
            <div className="stat-card">
              <h3>총 강의 수</h3>
              <p className="stat-number">{courses.length}</p>
            </div>
            <div className="stat-card">
              <h3>총 사용자 수</h3>
              <p className="stat-number">{users.length}</p>
            </div>
            <div className="stat-card">
              <h3>공개 강의 수</h3>
              <p className="stat-number">{courses.filter(course => course.isPublished).length}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard; 